import * as React from 'react';
import { connect } from 'react-redux';
import { SAVE_LITE_BOM_REQUEST } from 'actions';
import { SELECT_ACCESSORY, UNSELECT_ACCESSORY } from 'bom/bomActions';
import { UPDATE_LITE_PROJECT_REQUEST } from 'v3/dashboard/liteProjectActions';
import { hasLeftPanelValidationErrors } from 'v3/dashboard/liteProjectEpics';
import { getProductName, isRM10Evolution, isRmGridflex10D, isRmGridflex5Rwdi, isCommercialProduct } from '__common/constants/products';
import { SET_PROJECT_OPTION } from 'projectDesign/components/projectConfiguration/projectConfigurationActions';
import { RoofType } from 'projectDesign/components/projectConfiguration/utils/constants';
import { s5AttachmentPreferenceDict, isGeneric, S5_DEFAULT_ULTIMATE_LOAD_UPLIFT, S5_DEFAULT_ULTIMATE_LOAD_SHEAR } from 'projectDesign/components/projectConfiguration/utils/s5AttachmentsFilters';
import {
  renderConditionalAttachmentFields as renderSharedConditionalAttachmentFields,
  renderLiteAttachmentSelect,
} from 'v3/bomPage/liteAttachmentFields';
import AppHistory from '__common/modules/history';
import {
  BomPart,
  ATTACHMENT_TYPES,
  ATTACHMENT_TYPE_GROUPS,
  attachmentLabelToInt,
  attachmentIntToLabel,
  FINISHES,
  FINISH_COLOR_CODES,
  availableFinishes,
  finishOf,
  resolveVariant,
  partQtyDisplay,
  partQtyNumber,
  isAccessoryAdded,
  accessoryQtyNumber,
  accessoryQtyDisplay,
  isValidQtyInput,
  buildBomSavePayload,
  seedRowFinishFromSelections,
  getAugmentedPartsList,
  seedAccessoryQtysFromSelections,
  seedAccessoryQtysFromPartsList,
  cacheBomEditState,
  readBomEditCache,
  clearBomEditCache,
  sortLiteBom,
  isSelectedUAnchorPart,
} from 'v3/bomPage/liteBomEdits';
import RailTypeSelector from 'v3/bomPage/RailTypeSelector';

type Props = {
  domesticContent: boolean;
  onDomesticToggle: () => void;
  partsList: BomPart[];
  accessoriesList: BomPart[];
  totalModules: number;
  totalWatts: number;
  isLoading: boolean;
  // True after the first FETCH_BOM_PART_LIST_SUCCESS (the lite GET BOM lands
  // through that legacy action). BOM_RESET on workspace mount sets it back
  // to false, so this also functions as "BOM has never been fetched yet".
  bomReady: boolean;
  // True when there's a pending user edit that hasn't been autosaved yet, or
  // when an autosave PUT or follow-up BOM GET is in flight. Drives the
  // existing update veil plus the repositioned "Updating BOM…" status.
  isUpdating: boolean;
  // Truthy when the BOM fetch errored. Combined with bomReady, lets us
  // distinguish "still waiting" from "we tried, the request finished".
  bomError: any;
  // Wired from store/URL by the parent (DesignWorkspace passes projectId
  // from match.params; productId comes from projectConfiguration). Both are
  // needed to POST /api/v1/ub_lite/bom/<projectId>/.
  projectId?: string;
  productId?: number;
  extraParameters?: any;
  // Currently-selected attachment type as the backend integer code.
  // Wired via SET_PROJECT_OPTION; persisted by the autosave PUT body.
  attachmentTypeInt?: number;
  // Drives the per-(product, roof) compatible-attachment filter for the
  // dropdown. Different roof types use different legacy fields:
  // Shingle/R_PANEL/Others -> attachment_type, Standing Seam -> s5_or_sci,
  // Tile -> tile_replacement_or_solarhooks. Mirrors NxtHorizonConfig and
  // SmConfig's per-roof-type field selection.
  roofType?: number;
  s5OrSci?: string;
  tileReplacementOrSolarhooks?: number;
  railsProductId?: number;
  // Conditional attachment fields from projectEnvConfig
  railClampOption?: number;
  materialThickness?: number;
  s5RoofManufacturer?: string;
  s5RoofPanel?: string;
  s5RoofMaterial?: string;
  s5Clamp?: string;
  s5UltimateLoadUplift?: number;
  s5UltimateLoadShear?: number;
  roofSubstrate?: number;
  standoffHeight?: number;
  // Commercial (RM10/GF5/GF10) fields
  manufacturer?: string;
  colour?: string;
  allowMechanicalAttachments?: number;
  attachmentOptimizationCriteria?: number;
  upliftAllowable?: number;
  shearAllowable?: number;
  isUbuilderLite?: boolean;
  tilt?: number;
  railFinish?: string;
  dispatch?: Function;
  moduleLength?: number;
  snowLoad?: number;
  uanchorPartNumber?: string;
};

type State = {
  // Per-row finish selection. Keyed by base part number; value is "Mill" /
  // "Clear" / "Dark". Falls back to "Dark" if a row hasn't been touched.
  rowFinish: { [partNumber: string]: string };
  partListOpen: boolean;
  attachmentOpen: boolean;
  accessoriesOpen: boolean;
  // Held as a raw string for the same reason as accessoryQty — keeps the
  // input empty mid-edit instead of snapping to "0" on backspace.
  discountPct: string;
  // Local "added" tracker for accessories that the user has toggled on. Until
  // the BOM POST flow is wired, this is UI-only state separate from
  // `accessoriesList[].selected` (which comes from the server payload).
  addedAccessories: { [number: string]: boolean };
  // Per-accessory editable qty for the "added cards" above the catalog. Keyed
  // by part number; held as a raw string so the input can legitimately be
  // empty mid-edit (avoids the "type=number snaps to 0 then prepends digits"
  // bug). Falls back to the part's suggested_quantity for display when unset.
  accessoryQty: { [number: string]: string };
  // (attachment_type now lives on projectEnvConfig and is wired through
  // SET_PROJECT_OPTION — no local state needed.)
  // Toggled by the "expand" icon in the Part List header. When true, the
  // Part List rows are also mirrored into a centered modal for an enlarged
  // view (mirrors openBOMEditor() in the redesign reference HTML).
  partListExpanded: boolean;
  // Per-part editable qty (Part List + Attachment rows). Raw string so the
  // input can be empty mid-edit; coerced to int on POST (empty → 0).
  partQty: { [number: string]: string };
};

class BomPanel extends React.Component<Props, State> {
  state: State = {
    rowFinish: {},
    partListOpen: true,
    attachmentOpen: true,
    accessoriesOpen: true,
    discountPct: undefined,
    addedAccessories: {},
    accessoryQty: {},
    partListExpanded: false,
    partQty: {},
  };

  // Debounce timer for the POST. Set on every user-driven edit; clears on
  // unmount so a pending save doesn't fire after the panel goes away.
  private saveTimer: ReturnType<typeof setTimeout> | null = null;

  componentWillUnmount() {
    // After BOM_RESET / regen, local qty overlays are stale. Writing them
    // back would reapply the previous array's accessory quantities on the
    // next mount (Complete BOM page or panel remount).
    if (this.props.bomReady) {
      cacheBomEditState(this.props.projectId, this.state);
    } else {
      clearBomEditCache(this.props.projectId);
    }
    // Flush any pending edit before the panel goes away. Prevents the user
    // typing a qty, navigating off, and losing the change.
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      if (this.props.bomReady) {
        this.flushBomSave();
      }
    }
  }

  componentDidMount() {
    const cached = readBomEditCache(this.props.projectId);
    if (cached) {
      this.setState({
        rowFinish: cached.rowFinish,
        partQty: cached.partQty,
        addedAccessories: cached.addedAccessories,
        accessoryQty: cached.accessoryQty,
        discountPct: cached.discountPct,
      });
    }
    // Seed local rowFinish from extra_parameters.selections.color_finish
    // so a fresh mount restores the user's persisted finish choice — even
    // when an empty-BOM-POST regen has reset user_builds back to defaults
    // (which would otherwise make the dropdown silently snap to Mill).
    this.seedRowFinishFromProps();
  }

  componentDidUpdate(prevProps: Props) {
    // BOM_RESET (Clear All, satellite ↔ blank) and FETCH_BOM_PART_LIST_REQUEST
    // drop isReady. Local + module qty overlays would otherwise keep the
    // previous array's accessory quantities on matching part numbers.
    if (prevProps.bomReady && !this.props.bomReady) {
      clearBomEditCache(this.props.projectId);
      this.setState({
        partQty: {},
        accessoryQty: {},
        addedAccessories: {},
      });
    }
    if (
      prevProps.partsList !== this.props.partsList ||
      prevProps.extraParameters !== this.props.extraParameters ||
      prevProps.railFinish !== this.props.railFinish
    ) {
      this.seedRowFinishFromProps();
    }
    if (this.props.isUbuilderLite && this.props.roofType === RoofType.STANDING_SEAM && this.props.s5OrSci === s5AttachmentPreferenceDict.s_5) {
      if (
        this.props.s5RoofManufacturer !== isGeneric ||
        this.props.s5RoofPanel !== isGeneric ||
        this.props.s5RoofMaterial !== isGeneric ||
        !this.props.s5Clamp ||
        !this.props.s5UltimateLoadUplift ||
        !this.props.s5UltimateLoadShear
      ) {
        if (this.props.dispatch && this.props.projectId) {
          const nextClamp = this.props.s5Clamp || 'S-5-N Mini';
          const nextUplift = this.props.s5UltimateLoadUplift || S5_DEFAULT_ULTIMATE_LOAD_UPLIFT;
          const nextShear = this.props.s5UltimateLoadShear || S5_DEFAULT_ULTIMATE_LOAD_SHEAR;
          this.props.dispatch(SET_PROJECT_OPTION('s5_roof_manufacturer', isGeneric));
          this.props.dispatch(SET_PROJECT_OPTION('s5_roof_panel', isGeneric));
          this.props.dispatch(SET_PROJECT_OPTION('s5_roof_material', isGeneric));
          this.props.dispatch(SET_PROJECT_OPTION('s5_clamp', nextClamp));
          this.props.dispatch(SET_PROJECT_OPTION('s5_ultimate_load_uplift', nextUplift));
          this.props.dispatch(SET_PROJECT_OPTION('s5_ultimate_load_shear', nextShear));
          this.props.dispatch(UPDATE_LITE_PROJECT_REQUEST({ projectId: this.props.projectId, regenBom: true }));
        }
      }
    }
  }

  // Merge persisted color_finish into local state without clobbering any
  // edits the user has already made in this session (state.rowFinish wins).
  private seedRowFinishFromProps = () => {
    const { partsList, accessoriesList, extraParameters } = this.props;
    if (!partsList || partsList.length === 0) return;
    const seeded = seedRowFinishFromSelections(partsList, extraParameters);
    const seededAccessoryQty = seedAccessoryQtysFromSelections(accessoriesList || [], extraParameters);
    // Accessories returned in BOTH parts_list and accessories_list take their
    // qty from parts_list (the authoritative build quantity).
    const seededAccessoryQtyFromParts = seedAccessoryQtysFromPartsList(accessoriesList || [], partsList);
    const seededDiscountPct = (extraParameters && extraParameters.discount_pct !== undefined)
      ? String(extraParameters.discount_pct)
      : undefined;

    this.setState((prev) => {
      let changed = false;
      const nextRowFinish = { ...prev.rowFinish };
      let normalizedFinish = undefined;
      if (this.props.railFinish) {
        for (const finish of Object.keys(FINISH_COLOR_CODES)) {
          if ((FINISH_COLOR_CODES[finish] || []).includes(this.props.railFinish.toUpperCase())) {
            normalizedFinish = finish;
            break;
          }
        }
        if (!normalizedFinish) {
          normalizedFinish = FINISHES.find(f => f.toUpperCase() === this.props.railFinish.toUpperCase());
        }
      }

      Object.keys(seeded).forEach((k) => {
        if (nextRowFinish[k] === undefined) {
          nextRowFinish[k] = seeded[k];
          changed = true;
        }
      });

      // If we still have parts without a finish selection, use the project's preferred finish
      (partsList || []).forEach(p => {
        if (nextRowFinish[p.number] === undefined && normalizedFinish) {
          nextRowFinish[p.number] = normalizedFinish;
          changed = true;
        }
      });

      const nextAccessoryQty = { ...prev.accessoryQty };
      // parts_list qty wins for accessories present in both lists, but only
      // while the user hasn't toggled the accessory this session — a
      // remove + re-add must fall back to the accessories_list suggested qty
      // rather than re-pulling the parts_list value.
      Object.keys(seededAccessoryQtyFromParts).forEach((k) => {
        if (prev.addedAccessories[k] === undefined) {
          const newValue = seededAccessoryQtyFromParts[k];
          if (nextAccessoryQty[k] === undefined || (nextAccessoryQty[k] !== newValue && !this.props.isUpdating)) {
            nextAccessoryQty[k] = newValue;
            changed = true;
          }
        }
      });
      Object.keys(seededAccessoryQty).forEach((k) => {
        if (nextAccessoryQty[k] === undefined) {
          nextAccessoryQty[k] = seededAccessoryQty[k];
          changed = true;
        }
      });
      let nextDiscountPct = prev.discountPct;
      if (seededDiscountPct !== undefined && nextDiscountPct === undefined) {
        nextDiscountPct = seededDiscountPct;
        changed = true;
      }

      return changed
        ? { rowFinish: nextRowFinish, accessoryQty: nextAccessoryQty, discountPct: nextDiscountPct }
        : null;
    });
  }

  private setRowFinish = (partNumber: string, finish: string) => {
    this.setState(
      prev => ({ rowFinish: { ...prev.rowFinish, [partNumber]: finish } }),
      this.scheduleBomSave,
    );
  };

  private setPartQty = (number: string, raw: string) => {
    if (!isValidQtyInput(raw)) return;
    this.setState(
      prev => ({ partQty: { ...prev.partQty, [number]: raw } }),
      this.scheduleBomSave,
    );
  };

  // Debounced POST. Coalesces rapid keystrokes / clicks into a single save
  // 600 ms after the last edit. switchMap on the epic side guarantees only
  // the latest in-flight request completes if the user keeps typing.
  private scheduleBomSave = () => {
    cacheBomEditState(this.props.projectId, this.state);
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.flushBomSave();
    }, 600);
  };

  private flushBomSave = () => {
    const { projectId, productId, dispatch, partsList, accessoriesList, extraParameters, domesticContent, totalModules, isUpdating, moduleLength, snowLoad } = this.props;
    if (!projectId || !productId || !dispatch || isUpdating) return;
    // Guard against empty parts_list — the backend treats it as "wipe
    // user_builds" (see updateLiteProject epic's regen branch). The user
    // can't actually issue an edit when partsList is empty (no rows
    // visible), but a stray componentWillUnmount flush with an empty
    // slice would otherwise destroy server state.
    if (!(partsList && partsList.length > 0)) return;
    const augmentedPartsList = getAugmentedPartsList(partsList, productId, domesticContent, totalModules, moduleLength, snowLoad);
    const body = buildBomSavePayload({
      state: this.state,
      partsList: augmentedPartsList,
      accessoriesList: accessoriesList || [],
      extraParameters,
    });
    // Do NOT add chainFetch:true here. SAVE_LITE_BOM_REQUEST with
    // chainFetch chains a FETCH whose SUCCESS dispatches
    // FETCH_BOM_PART_LIST_SUCCESS — which refreshes bom.partsList and
    // bom.accessoriesList from the server. After an accessory toggle,
    // the backend can return the accessory in BOTH lists (as a part
    // AND a selected accessory), and the panel's subtotal would
    // double-count it. The legacy autosave on /bom/ routes would also
    // fire, adding a second write that can corrupt totals.
    //
    // Without chainFetch, bom.partsList stays at the pre-edit snapshot
    // while local state reflects the edit — subtotals are computed
    // correctly. CompleteBomPage's mount FETCH is the sync point when
    // the user navigates to that page.
    dispatch(SAVE_LITE_BOM_REQUEST({
      projectId,
      productId: getProductName(productId),
      body,
    }));
  };

  // Idempotent toggle: clicking an already-added accessory removes it (so a
  // double-click can't create duplicates the way the reference HTML did).
  private goToCompleteBom = () => {
    const { projectId, productId } = this.props;
    if (!projectId || !productId) return;
    const productName = getProductName(productId);
    if (!productName) return;
    AppHistory.push(`/project/lite/bom/${productName}/${projectId}`);
  }

  private toggleAccessory = (number: string) => {
    // Resolve the current added-state from the same predicate that
    // drives rendering — local override first, then Redux fallback.
    // Reading prev.addedAccessories alone misses the case where the
    // user is toggling an accessory that was added on the OTHER page:
    // local state is empty after mount, but a.selected in Redux says
    // it's added. Without this we'd treat the first click as an "add"
    // (no-op visually) instead of as a "remove".
    const accessory = (this.props.accessoriesList || []).find(a => a && a.number === number);
    const currentlyAdded = isAccessoryAdded(
      accessory || ({ number } as BomPart),
      this.state,
    );
    const nextAdded = !currentlyAdded;
    // Keep Redux's bom.accessoriesList[X].selected in sync with the
    // user's intent so the flag persists across unmount/remount when
    // the user navigates to the other page. The lite POST writes
    // user_builds but the server's accessories_list GET does not flip
    // the selected flag based on user_builds membership, so Redux is
    // the client-owned source of truth for cross-page sync.
    if (this.props.dispatch) {
      this.props.dispatch(nextAdded ? SELECT_ACCESSORY(number) : UNSELECT_ACCESSORY(number));
    }
    this.setState(
      prev => {
        const nextAddedMap = { ...prev.addedAccessories, [number]: nextAdded };
        const nextQty = { ...prev.accessoryQty };
        if (!nextAdded) delete nextQty[number];
        return { addedAccessories: nextAddedMap, accessoryQty: nextQty };
      },
      this.scheduleBomSave,
    );
  };

  // Accept only digits and the empty string. No coercion to 0 on backspace —
  // the empty buffer is preserved so the next keystroke replaces it cleanly.
  private setAccessoryQty = (number: string, raw: string) => {
    if (!isValidQtyInput(raw)) return;
    this.setState(
      prev => ({ accessoryQty: { ...prev.accessoryQty, [number]: raw } }),
      this.scheduleBomSave,
    );
  };

  // Card rendered above the catalog for each toggled-on accessory. Mirrors
  // the .bom-item layout in the reference HTML: title / partNo / sub /
  // 4-cell metrics grid (Qty, Suggested, Unit, Total) plus an × remove btn.
  private renderAccessoryAddedCard(a: BomPart, partsByNumber: Map<string, BomPart>) {
    const isToggledThisSession = this.state.addedAccessories[a.number] !== undefined;
    const buildPart = !isToggledThisSession ? partsByNumber.get(a.number) : undefined;
    const suggested = buildPart ? buildPart.suggested_quantity : (a.suggested_quantity || 0);
    const qty = accessoryQtyNumber(a, this.state, buildPart ? buildPart.suggested_quantity : undefined);
    const hidePrice = isSelectedUAnchorPart(a.number, this.props.uanchorPartNumber);
    const unitPrice = hidePrice ? 0 : Number(a.price);
    const total = unitPrice * qty;
    return (
      <div className="v3-bom-item" key={`added-${a.number}`}>
        <div className="v3-bom-item-top">
          <div className="v3-bom-item-info">
            <div className="v3-bom-item-title" title={a.description || a.name}>{a.description || a.name}</div>
            <div className="v3-bom-item-partnum">{a.number}</div>
            {a.full_description && a.full_description !== (a.description || a.name) && (
              <div className="v3-bom-item-sub">{a.full_description}</div>
            )}
            {hidePrice && <div className="v3-bom-pricing-note">Contact Unirac for Pricing</div>}
          </div>
          <button type="button" className="v3-bom-item-remove" title="Remove"
            onClick={() => this.toggleAccessory(a.number)}>×</button>
        </div>
        <div className="v3-bom-metrics">
          <div className="v3-bom-metric-cell">
            <span className="v3-bom-metric-lbl">Qty</span>
            <div className="v3-bom-qty-wrap">
              <input type="text" inputMode="numeric" pattern="[0-9]*"
                value={accessoryQtyDisplay(a, this.state, buildPart ? buildPart.suggested_quantity : undefined)}
                onChange={(e) => this.setAccessoryQty(a.number, e.target.value)} />
            </div>
          </div>
          <div className="v3-bom-metric-cell">
            <span className="v3-bom-metric-lbl">Suggested</span>
            <span className="v3-bom-metric-val muted">{suggested}</span>
          </div>
          <div className="v3-bom-metric-cell">
            <span className="v3-bom-metric-lbl">Unit $</span>
            <span className="v3-bom-metric-val">${unitPrice.toFixed(2)}</span>
          </div>
          <div className="v3-bom-metric-cell">
            <span className="v3-bom-metric-lbl">Total $</span>
            <span className="v3-bom-metric-val strong">${Number(total).toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  }

  // are merged into a single auto-sizing cell separated by a slash, and the
  // Finish dropdown moves into the left cell (under the part number) so it
  // can wrap to a second line on narrow widths instead of forcing a wider
  // panel. Parts without variants render only the name + part number.
  // Inline rail-type picker placed on each "Rail" row (sibling of the
  // Mill/Dark finish dropdown). All rail rows share the same system-level
  // choice, so we render it on every rail row rather than once above the
  // list — this matches the per-row attachment/finish visual pattern and
  // keeps the picker next to the parts it controls.
  private renderRailTypeInline = (part: BomPart) => {
    if (isCommercialProduct(this.props.productId)) return null;
    const isRailRow = part && part.type === 'Rail' && part.category === 'Base';
    if (!isRailRow) return null;
    return (
      <span className="v3-bom-rail-type-wrap" onClick={(e) => e.stopPropagation()}>
        <RailTypeSelector
          productId={this.props.productId}
          railsProductId={this.props.railsProductId}
          roofType={this.props.roofType}
          projectId={this.props.projectId}
          dispatch={this.props.dispatch}
        />
      </span>
    );
  };

  private renderRow(part: BomPart) {
    const resolved = resolveVariant(part, this.state, this.props.extraParameters);
    const qty = partQtyNumber(part, this.state);
    const hidePrice = isSelectedUAnchorPart(resolved.number, this.props.uanchorPartNumber)
      || isSelectedUAnchorPart(part.number, this.props.uanchorPartNumber);
    const unitPrice = hidePrice ? 0 : Number(resolved.price);
    const total = unitPrice * qty;
    // Only render the dropdown for finishes that actually have a matching
    // variant in this part's data — listing labels with no real underlying
    // variant produces the "select Clear, see Mill" silent-fallback bug.
    const finishOptions = availableFinishes(part);
    const hasFinish = finishOptions.length > 1;
    return (
      <div className="v3-bom-row" key={part.number}>
        <div className="v3-bom-row-inner">
          <div className="v3-bom-row-left">
            <div className="v3-bom-row-name" title={resolved.description}>{resolved.description}</div>
            <div className="v3-bom-row-partno" title={resolved.number}>{resolved.number}</div>
            {hidePrice && <div className="v3-bom-pricing-note">Contact Unirac for Pricing</div>}
            {hasFinish && (
              <select className="v3-bom-finish-sel v3-bom-finish-sel--short" value={finishOf(part, this.state, this.props.extraParameters)}
                onChange={(e) => this.setRowFinish(part.number, e.target.value)}>
                {finishOptions.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            )}
            {this.renderRailTypeInline(part)}
          </div>
          <div className="v3-bom-qty-wrap v3-bom-qty-sug-wrap">
            <input type="text" inputMode="numeric" pattern="[0-9]*"
              value={partQtyDisplay(part, this.state)}
              onChange={(e) => this.setPartQty(part.number, e.target.value)} />
            <span className="v3-bom-qty-slash">/</span>
            <span className="v3-bom-row-sug">{resolved.suggested_quantity}</span>
          </div>
          <span className="v3-bom-row-unit">${unitPrice.toFixed(2)}</span>
          <span className="v3-bom-row-total">${Number(total).toFixed(2)}</span>
        </div>
      </div>
    );
  }

  private renderColumnHeaders() {
    return (
      <div className="v3-bom-row v3-bom-row-col-hdr">
        <div className="v3-bom-row-inner">
          <div className="v3-bom-row-left" />
          <span className="v3-bom-col-lbl v3-bom-col-qtysug">Qty / Suggested</span>
          <span className="v3-bom-col-lbl v3-bom-col-unit">Unit $</span>
          <span className="v3-bom-col-lbl v3-bom-col-total">Total $</span>
        </div>
      </div>
    );
  }

  // Attachment section column header: the left cell hosts the shared UB
  // Lite attachment-type dropdown (grouped by roof family via the shared
  // renderLiteAttachmentSelect helper). Falls back to the static
  // ATTACHMENT_TYPE_GROUPS picker when the helper returns null.
  private renderAttachmentColumnHeaders() {
    const isCommercial = isCommercialProduct(this.props.productId);
    const shared = renderLiteAttachmentSelect({
      productId: this.props.productId,
      roofType: this.props.roofType,
      railsProductId: this.props.railsProductId,
      attachmentTypeInt: this.props.attachmentTypeInt,
      s5OrSci: this.props.s5OrSci,
      tileReplacementOrSolarhooks: this.props.tileReplacementOrSolarhooks,
      isUbuilderLite: !!this.props.isUbuilderLite,
      tilt: this.props.tilt,
      dispatch: this.props.dispatch,
      selectClassName: 'v3-bom-finish-sel',
      // BOM depends on these fields — fire an immediate PUT+wipe+GET so
      // the user sees the regenerated parts without waiting for the 5s
      // autosave tick, and so user_builds get wiped (otherwise the
      // chained GET would return stale parts). See updateLiteProject
      // epic's regenBom branch.
      onPersist: () => {
        if (this.props.dispatch && this.props.projectId) {
          this.props.dispatch(UPDATE_LITE_PROJECT_REQUEST({ projectId: this.props.projectId, regenBom: true }));
        }
      },
    });
    return (
      <div className="v3-bom-row v3-bom-row-col-hdr">
        <div className="v3-bom-row-inner">
          <div className="v3-bom-row-left">
            {isCommercial ? null : (shared || this.renderStaticAttachmentSelect())}
          </div>
          <span className="v3-bom-col-lbl v3-bom-col-qtysug">Qty / Suggested</span>
          <span className="v3-bom-col-lbl v3-bom-col-unit">Unit $</span>
          <span className="v3-bom-col-lbl v3-bom-col-total">Total $</span>
        </div>
      </div>
    );
  }

  // Delegates to the shared renderConditionalAttachmentFields helper.
  // The onFieldChange callback maps every field edit to BomPanel's
  // persistence flow (SET_PROJECT_OPTION + immediate PUT+regen).
  private renderConditionalAttachmentFields() {
    const p = this.props;
    return renderSharedConditionalAttachmentFields({
      roofType: p.roofType,
      productId: p.productId,
      attachmentTypeInt: p.attachmentTypeInt,
      s5OrSci: p.s5OrSci,
      tileReplacementOrSolarhooks: p.tileReplacementOrSolarhooks,
      railClampOption: p.railClampOption,
      materialThickness: p.materialThickness,
      roofSubstrate: p.roofSubstrate,
      standoffHeight: p.standoffHeight,
      manufacturer: p.manufacturer,
      colour: p.colour,
      isUbuilderLite: !!p.isUbuilderLite,
      allowMechanicalAttachments: p.allowMechanicalAttachments,
      attachmentOptimizationCriteria: p.attachmentOptimizationCriteria,
      upliftAllowable: p.upliftAllowable,
      shearAllowable: p.shearAllowable,
      onFieldChange: (apiField, value) => {
        if (!p.dispatch) return;
        p.dispatch(SET_PROJECT_OPTION(apiField, value));
        if (p.projectId) {
          p.dispatch(UPDATE_LITE_PROJECT_REQUEST({ projectId: p.projectId, regenBom: true }));
        }
      },
    });
  }

  // Fallback for products other than NXT/SM (none in Lite today, but
  // structurally we don't want to break the dropdown if a new product is
  // routed through this panel before getting its own legacy filter wired).
  private renderStaticAttachmentSelect() {
    return (
      <select
        className="v3-bom-finish-sel"
        value={attachmentIntToLabel(this.props.attachmentTypeInt, this.props.productId)}
        onChange={(e) => {
          const next = attachmentLabelToInt(e.target.value, this.props.productId);
          if (next !== undefined && this.props.dispatch) {
            this.props.dispatch(SET_PROJECT_OPTION('attachment_type', next));
            if (this.props.projectId) {
              this.props.dispatch(UPDATE_LITE_PROJECT_REQUEST({ projectId: this.props.projectId, regenBom: true }));
            }
          }
        }}>
        {ATTACHMENT_TYPE_GROUPS.map(g => (
          <optgroup key={g.label} label={g.label}>
            {g.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </optgroup>
        ))}
      </select>
    );
  }

  // Group accessories by `type` (the backend's category-ish field) and
  // render as a catalog. Each item has a "+" / checkmark indicator that
  // toggles local addedAccessories state. Until the POST flow is wired,
  // this is UI-only — useful for visual parity with the redesign mockup.
  private renderAccessoriesCatalog(items: BomPart[]) {
    if (items.length === 0) {
      return <div className="v3-bom-acc-empty">No accessories available.</div>;
    }
    const groups: { [cat: string]: BomPart[] } = {};
    items.forEach(item => {
      const cat = item.type || 'Other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    const categoryOrder = Object.keys(groups);
    return (
      <div className="v3-acc-catalog-inline">
        {categoryOrder.map((cat, idx) => (
          <React.Fragment key={cat}>
            {idx > 0 && <div className="v3-acc-picker-divider" />}
            <div className="v3-acc-picker-cat-title">{cat}</div>
            {groups[cat].map(item => {
              const added = isAccessoryAdded(item, this.state);
              const label = item.description || item.name;
              const desc = item.full_description && item.full_description !== label
                ? item.full_description
                : null;
              return (
                <button key={item.number} type="button"
                  className="v3-acc-picker-item"
                  onClick={() => this.toggleAccessory(item.number)}>
                  <span className="v3-acc-picker-text">
                    <span className="v3-acc-picker-label">{label}</span>
                    {desc && <span className="v3-acc-picker-desc">{desc}</span>}
                  </span>
                  <span className="v3-acc-picker-price">${Number(item.price).toFixed(2)}</span>
                  <span className={`v3-acc-item-indicator ${added ? 'checked' : ''}`}>
                    {added ? '✓' : '+'}
                  </span>
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    );
  }

  renderBomLoading() {
    return (
      <div className="v3-right-panel">
        <div className="v3-rp-header">
          <div className="v3-rp-title">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2h10v10H2zM2 5h10M2 8h10M5 2v10" stroke="currentColor" strokeWidth="1.3" />
            </svg>
            Bill of Materials
          </div>
        </div>
        <div className="v3-rp-bom-loading">
          <div className="v3-rp-bom-loading-spinner" aria-hidden="true">
            <svg width="32" height="32" viewBox="0 0 50 50" fill="none">
              <circle cx="25" cy="25" r="20" stroke="rgba(0,59,92,.15)" strokeWidth="4" />
              <path d="M25 5 a20 20 0 0 1 20 20" stroke="#003B5C" strokeWidth="4" strokeLinecap="round" />
            </svg>
          </div>
          <div className="v3-rp-bom-loading-msg">Loading BOM…</div>
          <div className="v3-rp-bom-loading-sub">Fetching parts list from the server.</div>
        </div>
      </div>
    );
  }

  render() {
    const { domesticContent, onDomesticToggle, partsList: propPartsList, accessoriesList, totalModules, totalWatts, isLoading, bomReady, bomError, projectId, isUpdating, productId } = this.props;
    const { partListOpen, attachmentOpen, accessoriesOpen, discountPct } = this.state;

    // Show a loading state until the first GET BOM completes (success or
    // error). state.bom is wiped by BOM_RESET on workspace mount, so a
    // missing isReady AND no error means we haven't heard back from the
    // first FETCH_LITE_BOM yet — without this gate the panel would render
    // "0 panels / $0.00" misleadingly until the first autosave triggers
    // the BOM fetch a few seconds later. Only gate when there's actually
    // a project to fetch BOM for AND there are panels — fresh projects
    // and post-Clear-All states never trigger a BOM GET, so we should
    // fall straight through to the empty-state render instead of hanging
    // on the loader forever.
    if (projectId && !bomReady && !bomError && totalModules > 0) {
      return this.renderBomLoading();
    }

    const { moduleLength, snowLoad } = this.props;
    const partsList = getAugmentedPartsList(propPartsList, productId, domesticContent, totalModules, moduleLength, snowLoad);

    // The backend's GET response can include an added accessory in
    // BOTH partsList AND accessoriesList. Filter accessory-numbered
    // items out of the parts-side sum and rendered sections so they
    // contribute exactly once — via the Accessories section.
    const accessoryNumberSet = new Set((accessoriesList || []).map(a => a && a.number).filter(Boolean));
    const partsByNumber = new Map(partsList.map(p => [p.number, p]));
    const sortedParts = sortLiteBom(productId, partsList);
    const baseParts = sortedParts.filter(p => !ATTACHMENT_TYPES.has(p.type) && !accessoryNumberSet.has(p.number));
    const attachmentParts = sortedParts.filter(p => ATTACHMENT_TYPES.has(p.type) && !accessoryNumberSet.has(p.number));

    const subtotal = partsList.reduce((sum, p) => {
      if (accessoryNumberSet.has(p.number)) return sum;
      if (isSelectedUAnchorPart(p.number, this.props.uanchorPartNumber)) return sum;
      const v = resolveVariant(p, this.state, this.props.extraParameters);
      if (isSelectedUAnchorPart(v.number, this.props.uanchorPartNumber)) return sum;
      return sum + Number(v.price) * partQtyNumber(p, this.state);
    }, 0) + (accessoriesList || []).reduce((sum, a) => {
      if (!isAccessoryAdded(a, this.state)) return sum;
      if (isSelectedUAnchorPart(a.number, this.props.uanchorPartNumber)) return sum;
      return sum + (a.price * accessoryQtyNumber(a, this.state));
    }, 0);
    const discountPctNum = Math.max(0, Math.min(100, parseInt(discountPct, 10) || 0));
    const discountAmt = subtotal * (discountPctNum / 100);
    const grandTotal = subtotal - discountAmt;

    const kwDc = totalWatts > 0 ? (totalWatts / 1000).toFixed(2) : '0.0';

    return (
      <div className="v3-right-panel">
        {/* ── HEADER ── */}
        <div className="v3-rp-header">
          <div className="v3-rp-title">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2h10v10H2zM2 5h10M2 8h10M5 2v10" stroke="currentColor" strokeWidth="1.3" />
            </svg>
            Bill of Materials
          </div>
          <div className="v3-dc-switch" onClick={onDomesticToggle}>
            <img
              src={require('assets/media/logos/Domestic_content_flag.png')}
              alt="U-Builder"
              style={{ height: 32, width: 'auto', display: 'block' }}
            />
            <div className="v3-dc-switch-info">
              <span className="v3-dc-switch-lbl">Domestic Content</span>
              {/* <span className="v3-dc-switch-sub">US installations only.</span> */}
            </div>
            <button type="button" className={`v3-dc-pill ${domesticContent ? 'active' : ''}`}
              role="switch" aria-checked={domesticContent}
              onClick={(e) => { e.stopPropagation(); onDomesticToggle(); }}>
              <span className="v3-dc-pill-thumb" />
            </button>
          </div>
        </div>

        {/* ── SUMMARY (3 stats: Panels / kW DC / Total Price) ── */}
        <div className="v3-rp-summary-bar">
          <div className="v3-rp-stat">
            <div className="v3-rp-stat-val">{totalModules}</div>
            <div className="v3-rp-stat-lbl">Panels</div>
          </div>
          <div className="v3-rp-stat">
            <div className="v3-rp-stat-val" style={{ color: '#1A9E6E' }}>{kwDc}</div>
            <div className="v3-rp-stat-lbl">kW DC</div>
          </div>
          <div className="v3-rp-stat">
            <div className="v3-rp-stat-val" style={{ color: '#FF6900' }}>${grandTotal.toFixed(2)}</div>
            <div className="v3-rp-stat-lbl">Total Price</div>
          </div>
        </div>

        {/* ── SCROLLABLE BOM BODY ── */}
        <div className="v3-rp-body">
          {/* Attachment Section (top per redesign) */}
          <div className={`v3-bom-section ${attachmentOpen ? 'open' : ''}`}>
            <div className="v3-bom-section-hdr" onClick={() => this.setState((p) => ({ attachmentOpen: !p.attachmentOpen }))}>
              <div className="v3-bom-section-icon" style={{ background: 'rgba(255,105,0,.1)', color: '#FF6900' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M6 1v8M3 6l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 10h8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              </div>
              <span className="v3-bom-section-title">Attachment</span>
              {isUpdating && bomReady && totalModules > 0 && (
                <div className="v3-rp-bom-update-chip v3-rp-bom-update-chip--inline" aria-live="polite">
                  <div className="v3-rp-bom-update-spinner" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 50 50" fill="none">
                      <circle cx="25" cy="25" r="20" stroke="rgba(255,255,255,.3)" strokeWidth="5" />
                      <path d="M25 5 a20 20 0 0 1 20 20" stroke="#FFFFFF" strokeWidth="5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <span className="v3-rp-bom-update-msg">Updating BOM…</span>
                </div>
              )}
              <svg className="v3-bom-section-chev" width="10" height="7" viewBox="0 0 10 7" fill="none">
                <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <button type="button" className="v3-bom-pl-expand"
                onClick={(e) => { e.stopPropagation(); this.setState({ partListExpanded: true }); }}
                title="Expand BOM">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M8 1h4v4M5 12H1V8M12 1L7.5 5.5M1 12l4.5-4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            {attachmentOpen && (
              <div className="v3-bom-section-body">
                {this.renderAttachmentColumnHeaders()}
                {this.renderConditionalAttachmentFields()}
                {attachmentParts.length === 0 ? (
                  <div className="v3-bom-acc-empty">No attachments yet.</div>
                ) : (
                  attachmentParts.map(p => this.renderRow(p))
                )}
              </div>
            )}
          </div>

          {/* Part List Section */}
          <div className={`v3-bom-pl-section ${partListOpen ? 'open' : ''}`} id="v3-bom-part-list">
            <div className="v3-bom-pl-hdr" onClick={() => this.setState((p) => ({ partListOpen: !p.partListOpen }))}>
              <div className="v3-bom-section-icon" style={{ background: 'rgba(34,132,195,.1)', color: '#2284c3' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M1 3h10M1 6h10M1 9h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
              </div>
              <span className="v3-bom-pl-title">Part List</span>
              <svg className="v3-bom-pl-chev" width="10" height="7" viewBox="0 0 10 7" fill="none">
                <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {partListOpen && (
              <div className="v3-bom-pl-body">
                {this.renderColumnHeaders()}
                {isLoading && partsList.length === 0 ? (
                  <div className="v3-bom-acc-empty">Loading BOM…</div>
                ) : baseParts.length === 0 ? (
                  <div className="v3-bom-acc-empty">Drop panels on the canvas to generate a BOM.</div>
                ) : (
                  baseParts.map(p => this.renderRow(p))
                )}
              </div>
            )}
          </div>

          {/* Accessories Section — catalog-style with category headers */}
          <div className={`v3-bom-section ${accessoriesOpen ? 'open' : ''}`}>
            <div className="v3-bom-section-hdr" onClick={() => this.setState((p) => ({ accessoriesOpen: !p.accessoriesOpen }))}>
              <div className="v3-bom-section-icon" style={{ background: 'rgba(0,59,92,.08)', color: '#003B5C' }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M9.5 2.5L7 5M5 7l-2.5 2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                </svg>
              </div>
              <span className="v3-bom-section-title">Accessories</span>
              <svg className="v3-bom-section-chev" width="10" height="7" viewBox="0 0 10 7" fill="none">
                <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {accessoriesOpen && (
              <div className="v3-bom-section-body">
                {(accessoriesList || [])
                  .filter(a => isAccessoryAdded(a, this.state))
                  .map(a => this.renderAccessoryAddedCard(a, partsByNumber))}
                {this.renderAccessoriesCatalog(accessoriesList || [])}
              </div>
            )}
          </div>

        </div>

        {/* Discount + Total Price wrap — outside scrollable body so it sticks to bottom */}
        <div className="v3-rp-totals-wrap">
          <div className="v3-rp-discount-row">
            <div className="v3-rp-discount-left">
              <span className="v3-rp-discount-lbl">Discount</span>
              <input type="text" inputMode="numeric" pattern="[0-9]*"
                className="v3-rp-discount-input" value={discountPct || ''} placeholder="0"
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw !== '' && !/^\d+$/.test(raw)) return;
                  if (raw !== '' && parseInt(raw, 10) > 100) return;
                  this.setState({ discountPct: raw }, this.scheduleBomSave);
                }} />
              <span className="v3-rp-discount-lbl">%</span>
            </div>
            <span className="v3-rp-discount-amt">−${discountAmt.toFixed(2)}</span>
          </div>
          <div className="v3-rp-total-row grand">
            <span>Total Price</span>
            <span className="v3-rp-total-val v3-orange">${grandTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div className="v3-rp-footer">
          <div className="v3-rp-export-btns">
            <button type="button" className="v3-export-btn-primary" style={{ flex: 1, justifyContent: 'center' }}
              disabled={totalModules === 0}
              onClick={this.goToCompleteBom}>
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M2 9v2h9V9M6.5 2v7M4 6l2.5 3L9 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Complete BOM
            </button>
          </div>
        </div>

        {/* ── EXPANDED PART LIST MODAL ── */}
        {this.state.partListExpanded && (
          <div className="v3-modal-overlay" onClick={() => this.setState({ partListExpanded: false })}>
            <div className="v3-modal-card v3-bom-expand-card" onClick={(e) => e.stopPropagation()}>
              <div className="v3-modal-top">
                <div className="v3-modal-title">Parts &amp; Attachments</div>
                <button type="button" className="v3-modal-close"
                  onClick={() => this.setState({ partListExpanded: false })} title="Close">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
              <div className="v3-modal-body v3-bom-expand-body">
                {/* Attachment */}
                <div className="v3-bom-section open">
                  <div className="v3-bom-section-body">
                    {this.renderAttachmentColumnHeaders()}
                    {this.renderConditionalAttachmentFields()}
                    {attachmentParts.length === 0 ? (
                      <div className="v3-bom-acc-empty">No attachments yet.</div>
                    ) : (
                      attachmentParts.map(p => this.renderRow(p))
                    )}
                  </div>
                </div>
                {/* Part List */}
                <div className="v3-bom-pl-section open">
                  <div className="v3-bom-pl-body">
                    {this.renderColumnHeaders()}
                    {baseParts.length === 0 ? (
                      <div className="v3-bom-acc-empty">Drop panels on the canvas to generate a BOM.</div>
                    ) : (
                      baseParts.map(p => this.renderRow(p))
                    )}
                  </div>
                </div>
                {/* Accessories */}
                <div className="v3-bom-section open">
                  <div className="v3-bom-section-body">
                    {(accessoriesList || [])
                      .filter(a => isAccessoryAdded(a, this.state))
                      .map(a => this.renderAccessoryAddedCard(a, partsByNumber))}
                    {this.renderAccessoriesCatalog(accessoriesList || [])}
                  </div>
                </div>
              </div>
              <div className="v3-modal-footer v3-bom-expand-footer">
                <div className="v3-rp-total-row grand v3-bom-expand-total">
                  <span>Total Price</span>
                  <span className="v3-rp-total-val v3-orange">${grandTotal.toFixed(2)}</span>
                </div>
                <div className="v3-bom-expand-actions">
                  <button type="button" className="v3-export-btn-ghost"
                    onClick={() => this.setState({ partListExpanded: false })}>Close</button>
                  <button type="button" className="v3-export-btn-primary v3-bom-expand-cta"
                    disabled={totalModules === 0}
                    onClick={() => { this.setState({ partListExpanded: false }); this.goToCompleteBom(); }}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <path d="M2 9v2h9V9M6.5 2v7M4 6l2.5 3L9 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Complete BOM
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preserve the original update veil and interaction lock. The
            visible loading status now stays beside the Attachment heading. */}
        {isUpdating && bomReady && totalModules > 0 && (
          <div className="v3-rp-bom-update-overlay" aria-hidden="true" />
        )}

      </div>
    );
  }
}

function mapStateToProps(appState: any) {
  const bom = appState.bom || {};
  const pc = appState.projectConfiguration || {};
  const env = pc.projectEnvConfig || {};
  const app = appState.app || {};
  const liteProject = appState.liteProject || {};
  // Four signals that combine into "BOM refresh in flight":
  //   1. liteDirty — user just edited; the 5s autosave hasn't fired yet.
  //   2. liteProject.updating — autosave PUT (or BOM POST) is in flight.
  //   3. liteProject.bomRefreshing — chained GET BOM after PUT success is
  //      in flight. Bridges the gap between PUT-success (updating=false)
  //      and the GET completing; without this the overlay drops too early
  //      and the user sees stale parts until the GET returns.
  //   4. bom.isLoading — legacy FETCH_BOM_PART_LIST_REQUEST flag (kept so
  //      the overlay still appears if any non-Lite code path triggers it).
  // The render guards on bomReady so this never appears during initial load.
  // When the left panel has active validation errors the autosave PUT is
  // short-circuited in liteProjectEpics (no PUT, no BOM regen, no GET), so
  // there is no in-flight refresh to wait for. liteDirty stays true while
  // the user is in the error state (it's re-marked on
  // UPDATE_LITE_PROJECT_FAILURE so a fix naturally retries), which would
  // otherwise pin the "Updating BOM" overlay on indefinitely. Suppress the
  // overlay in that case.
  const validationBlocked = hasLeftPanelValidationErrors(appState);
  const isUpdating = !validationBlocked && (!!app.liteDirty || !!liteProject.updating || !!liteProject.bomRefreshing || !!bom.isLoading);
  // Live, project-wide panel count. The active roof's panels live in
  // `panels.panels` (mutates synchronously on add/remove/undo); other roofs'
  // counts are cached on `drawingManager.roofs[id].panels` (snapshotted on
  // roof switch). Summing the two gives a project-wide total that still
  // updates immediately while editing — bom.total_modules only refreshes
  // after autosave + GET /bom roundtrip.
  const roofs = (appState.drawingManager && appState.drawingManager.roofs) || {};
  const selectedRoofId = appState.background && appState.background.selectedRoofId;
  const activePanels = (appState.panels && appState.panels.panels) || [];
  let totalModules = Array.isArray(activePanels) ? activePanels.length : 0;
  Object.keys(roofs).forEach((roofId) => {
    if (String(roofId) === String(selectedRoofId)) return;
    const rp = roofs[roofId] && roofs[roofId].panels;
    if (Array.isArray(rp)) totalModules += rp.length;
  });
  const ms = appState.moduleSelector || {};
  const perModuleWatts = (env.module_specs && env.module_specs.output)
    || (ms.modelData && Number(ms.modelData.watts))
    || env.module_watts
    || 0;
  return {
    partsList: bom.partsList || [],
    accessoriesList: bom.accessoriesList || [],
    totalModules,
    totalWatts: totalModules * Number(perModuleWatts || 0),
    isLoading: !!bom.isLoading,
    bomReady: !!bom.isReady,
    bomError: bom.error || null,
    isUpdating,
    productId: pc.productId,
    extraParameters: bom.extraParameters || {},
    attachmentTypeInt: env.attachment_type,
    roofType: env.roof_type,
    s5OrSci: env.s5_or_sci,
    tileReplacementOrSolarhooks: env.tile_replacement_or_solarhooks,
    railsProductId: pc.railsProductId,
    railClampOption: env.rail_clamp_option,
    materialThickness: env.material_thickness,
    s5RoofManufacturer: env.s5_roof_manufacturer,
    s5RoofPanel: env.s5_roof_panel,
    s5RoofMaterial: env.s5_roof_material,
    s5Clamp: env.s5_clamp,
    s5UltimateLoadUplift: env.s5_ultimate_load_uplift,
    s5UltimateLoadShear: env.s5_ultimate_load_shear,
    roofSubstrate: env.roof_substrate,
    standoffHeight: env.standoff_height,
    manufacturer: env.manufacturer,
    colour: env.colour,
    allowMechanicalAttachments: env.allow_mechanical_attachments,
    attachmentOptimizationCriteria: env.attachment_optimization_criteria,
    upliftAllowable: env.uplift_allowable,
    shearAllowable: env.shear_allowable,
    railFinish: env.rail_finish,
    isUbuilderLite: !!app.isUbuilderLite,
    tilt: env.tilt,
    moduleLength: (ms.modelData && Number(ms.modelData.height)) || env.module_length || 0,
    snowLoad: env.snow_load || 0,
    uanchorPartNumber: env.uanchor_part_number,
  };
}

export default connect(mapStateToProps)(BomPanel);
